import React, { useState, useRef } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Button } from '../buttons/button';

function getCroppedImg(image, crop, fileName, scale = 1, rotate = 0) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  const pixelRatio = window.devicePixelRatio || 1;

  canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
  canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

  ctx.scale(pixelRatio, pixelRatio);
  ctx.imageSmoothingQuality = 'high';

  const cropX = crop.x * scaleX;
  const cropY = crop.y * scaleY;

  const rotateRads = (rotate * Math.PI) / 180;
  const centerX = image.naturalWidth / 2;
  const centerY = image.naturalHeight / 2;

  ctx.save();
  ctx.translate(-cropX, -cropY);
  ctx.translate(centerX, centerY);
  ctx.rotate(rotateRads);
  ctx.scale(scale, scale);
  ctx.translate(-centerX, -centerY);
  ctx.drawImage(
    image,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight
  );
  ctx.restore();

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        return;
      }
      blob.name = fileName;
      resolve(blob);
    }, 'image/jpeg');
  });
}

const ImageCropModal = ({ isOpen, onClose, imageSrc, onCropComplete }) => {
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const imgRef = useRef(null);

  function onImageLoad(e) {
    const { width, height } = e.currentTarget;
    const newCrop = centerCrop(
      makeAspectCrop({ unit: '%', width: 90 }, 1, width, height),
      width,
      height
    );
    setCrop(newCrop);
  }

  const handleSaveCrop = () => {
    if (completedCrop?.width && completedCrop?.height && imgRef.current) {
      getCroppedImg(
        imgRef.current,
        completedCrop,
        'newProfile.jpeg',
        scale,
        rotate
      ).then((croppedImageBlob) => {
          onCropComplete(croppedImageBlob);
      });
    }
  };
  
  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999] p-4 animate-fade-in">
      <div className="rounded-2xl shadow-2xl w-full max-w-md mx-auto transform transition-all duration-300 scale-100 relative">
        <div className="p-6 border-b border-gray-200">
          <h3 className="font-semibold text-xl text-gray-800">Edit Profile Picture</h3>
          <p className="text-sm text-gray-500 mt-1">Adjust your photo to fit perfectly.</p>
        </div>
        <div className="p-6">
          <div className="bg-gray-100 rounded-lg overflow-hidden h-72 flex items-center justify-center">
            {imageSrc && (
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={1}
                circularCrop
              >
                <img
                  ref={imgRef}
                  alt="Crop me"
                  src={imageSrc}
                  style={{ transform: `scale(${scale}) rotate(${rotate}deg)` }}
                  onLoad={onImageLoad}
                />
              </ReactCrop>
            )}
          </div>
          <div className="space-y-4 mt-6">
            <div className="grid grid-cols-[auto,1fr,auto] items-center gap-3">
              <span className="text-sm font-medium text-gray-600">Zoom</span>
              <input
                type="range"
                value={scale}
                min="1"
                max="2"
                step="0.01"
                onChange={(e) => setScale(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>
            <div className="grid grid-cols-[auto,1fr,auto] items-center gap-3">
              <span className="text-sm font-medium text-gray-600">Rotate</span>
              <input
                type="range"
                value={rotate}
                min="-180"
                max="180"
                onChange={(e) => setRotate(Math.min(180, Math.max(-180, Number(e.target.value))))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>
          </div>
        </div>
        <div className="p-6 bg-gray-50 border-t border-gray-200 rounded-b-2xl flex gap-3">
          <Button onClick={onClose} variant="secondary" className="flex-1">
            Cancel
          </Button>
          <Button onClick={handleSaveCrop} variant="primary" className="flex-1">
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ImageCropModal;